import '@fontsource/instrument-serif';

import Flash from '@/components/flash';
import CaprockMark from '@/components/linear/caprock-mark';
import { Toaster } from '@/components/ui/toaster';
import { root_path } from '@/rails/routes';
import { Head, usePage } from '@inertiajs/react';
import { useEffect, type ReactNode } from 'react';

interface PublicLayoutProps {
  children: ReactNode;
  flash: { notice?: string; error?: string };
  pageTitle?: string;
}

// The pay page says whether its app is watermarked at the top of its props.
// The published app's front door says it on the app.
interface PublicPageProps {
  pageTitle?: string;
  watermarked?: boolean;
  app?: { watermarked?: boolean };
  [key: string]: unknown;
}

// Chrome for pages a customer of an app sees: the invoice pay page and a
// published app's front door. It carries the app owner's content, so the
// CAP Rock Labs mark stays small and in the footer, and only a watermarked
// app (the Start plan) shows it at all.
export default function PublicLayout({ children, flash, pageTitle }: PublicLayoutProps) {
  const { props } = usePage<PublicPageProps>();
  const title = pageTitle || props.pageTitle || 'CAP Rock Labs';
  const watermarked = props.watermarked ?? props.app?.watermarked ?? false;

  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.removeAttribute('data-linear-theme');
  }, []);

  return (
    <>
      <Head title={title} />
      <div data-authority className="bg-background text-foreground flex min-h-screen flex-col">
        <main className="mx-auto w-full max-w-2xl flex-1 px-6 py-12 sm:py-16">{children}</main>
        {watermarked ? (
          <footer className="border-t">
            <div className="text-muted-foreground mx-auto flex max-w-2xl items-center gap-2 px-6 py-5 text-xs">
              <CaprockMark size={16} />
              <a href={root_path()} className="hover:text-foreground">
                CAP Rock Labs
              </a>
            </div>
          </footer>
        ) : null}
        <Flash {...flash} />
        <Toaster />
      </div>
    </>
  );
}
